/**
 * AbilityCard - Display Pokemon abilities with descriptions
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { usePokemon } from '../../hooks/usePokemon';
import { useApp } from '../../context/AppContext';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';

function AbilityItem({ ability, isHidden, index }) {
  const { t } = useApp();
  const { getAbility, getLocalizedName, getLocalizedEffect } = usePokemon();
  const [abilityData, setAbilityData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    let mounted = true;
    
    const fetchAbility = async () => {
      const data = await getAbility(ability.ability.name);
      if (mounted) {
        setAbilityData(data);
        setLoading(false);
      }
    };
    
    fetchAbility();
    return () => { mounted = false; };
  }, [ability.ability.name, getAbility]);
  
  if (loading) {
    return (
      <div className="p-3 bg-muted/50 rounded-lg space-y-2">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-4 w-full" />
      </div>
    );
  }
  
  const localizedName = getLocalizedName(abilityData?.names) || ability.ability.name;
  const effect = getLocalizedEffect(abilityData?.effect_entries);
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      data-testid={`ability-${ability.ability.name}`}
      className={`p-3 rounded-lg ${
        isHidden 
          ? 'bg-gradient-to-r from-violet-100 to-rose-100 dark:from-violet-900/30 dark:to-rose-900/30 border border-violet-200 dark:border-violet-800' 
          : 'bg-muted/50'
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <h4 className="font-semibold capitalize text-foreground">
          {localizedName.replace(/-/g, ' ')}
        </h4>
        {isHidden && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-violet-500/20 text-violet-700 dark:text-violet-300">
            <Sparkles className="w-3 h-3 mr-1" />
            {t('hiddenAbility')}
          </Badge>
        )}
      </div>
      {effect && (
        <p className="text-sm text-muted-foreground leading-relaxed">
          {effect}
        </p>
      )}
    </motion.div>
  );
}

export function AbilityList({ abilities }) {
  const { t } = useApp();
  
  if (!abilities || abilities.length === 0) {
    return null;
  }
  
  // Sort abilities: regular first, hidden last
  const sortedAbilities = [...abilities].sort((a, b) => {
    if (a.is_hidden === b.is_hidden) return 0;
    return a.is_hidden ? 1 : -1;
  });
  
  return (
    <div className="space-y-3" data-testid="abilities-list">
      <h3 className="text-lg font-bold text-foreground">{t('abilities')}</h3>
      <div className="space-y-2">
        {sortedAbilities.map((ability, index) => (
          <AbilityItem
            key={ability.ability.name}
            ability={ability}
            isHidden={ability.is_hidden}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}

export default AbilityList;
